<!--
  D3 知识图谱画布容器：内部动态插入 <canvas>，无额外 DOM 节点。
  ref="wrap" 供测量宽高、截图、清空画布使用。
-->
<template>
    <div ref="wrap" class="d3-knowledge-graph complex-d3DIV"></div>
</template>

<script>
/**
 * D3KnowledgeGraph — 力导向知识图谱（Canvas 渲染）
 *
 * 流程概览：
 * 1. 根据 graphData 构建节点副本并着色（node.degree → 颜色）
 * 2. 按 degree 聚类，在圆周上分配簇中心，力导向若干 tick 后固定坐标
 * 3. 用 Canvas 绘制边、边标签、节点圆与文字
 * 4. d3.zoom 平移缩放；节点可拖拽；悬停高亮相邻边
 */
import * as d3 from 'd3';

/** 为节点生成稳定字符串 ID，供力导向 link.id 与 Map 查找 */
function nodeId(node, index) {
    if (node == null) return `__${index}`;
    if (node.id != null && node.id !== '') return String(node.id);
    if (node.name != null && node.name !== '') return String(node.name);
    return `__n${index}`;
}

/** 边端点：支持对象或字符串；对象时与 nodeId 规则一致 */
function linkEndpointKey(v, indexHint) {
    if (v == null) return null;
    if (typeof v === 'object') {
        return nodeId(v, indexHint);
    }
    return String(v);
}

/** 点到线段距离²，用于边命中测试 */
function dist2PointToSegment(px, py, x1, y1, x2, y2) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len2 = dx * dx + dy * dy;
    if (len2 < 1e-9) {
        const a = px - x1;
        const b = py - y1;
        return a * a + b * b;
    }
    let t = ((px - x1) * dx + (py - y1) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const qx = x1 + t * dx;
    const qy = y1 + t * dy;
    const a = px - qx;
    const b = py - qy;
    return a * a + b * b;
}

export default {
    name: 'D3KnowledgeGraph',
    props: {
        /** 图数据：nodes（含 degree、name/label）、edges（source/target 或 startNode/endNode） */
        graphData: {
            type: Object,
            default() {
                return { nodes: [], edges: [] };
            },
        },
        /** hidden：边标签默认透明，悬停显示；show：常显半透明 */
        sideLabel: {
            type: String,
            default: 'hidden',
        },
        /**
         * 悬停节点时，“无关元素”的透明度（0~1）
         * - 1：不变淡
         * - 0：完全不可见
         */
        hoverDimOpacity: {
            type: Number,
            default: 0.15,
        },
        /** true：不覆盖节点颜色，沿用 graphData 中 style（计量分析等） */
        preserveNodeStyle: {
            type: Boolean,
            default: false,
        },
        /** 自定义分簇键；不传则按 node.degree 字符串分簇（知识图谱） */
        clusterKeyFn: {
            type: Function,
            default: null,
        },
        /** true：半径用节点 _r（或 size/2），否则固定 20px */
        variableRadius: {
            type: Boolean,
            default: false,
        },
        linkDistance: {
            type: Number,
            default: 95,
        },
        layoutTicks: {
            type: Number,
            default: 480,
        },
        layoutCharge: {
            type: Number,
            default: -200,
        },
        layoutClusterStrength: {
            type: Number,
            default: 0.38,
        },
        /** 簇中心环半径 = min(w,h) * 该比例 */
        ringRadiusRatio: {
            type: Number,
            default: 0.3,
        },
        /** 初始随机偏移（像素） */
        initialJitter: {
            type: Number,
            default: 60,
        },
        /** >0 时节点文字超长截断并加 … */
        nodeLabelMaxLen: {
            type: Number,
            default: 0,
        },
        /** d3.zoom 缩放范围 [min, max] */
        zoomScaleExtent: {
            type: Array,
            default() {
                return [0.15, 5];
            },
        },
    },
    data() {
        return {
            graphWidth: 0, // 容器内容宽度（px）
            graphHeight: 0, // 容器内容高度（px）
            _d3Simulation: null, // 力模拟引用（tick 结束后会置空）
            _graphTeardown: null, // 卸载 canvas 事件与 zoom 的清理函数
        };
    },
    watch: {
        /** 数据或边标签策略变化时重绘整张图 */
        graphData: {
            handler() {
                this.$nextTick(() => this.renderGraph());
            },
            deep: true,
        },
        sideLabel() {
            this.$nextTick(() => this.renderGraph());
        },
        /** 布局参数、悬停变淡、缩放范围等变化时重绘 */
        layoutSignature() {
            this.$nextTick(() => this.renderGraph());
        },
    },
    computed: {
        /** 供 watch：任一布局相关 prop 变化则重算图谱 */
        layoutSignature() {
            const z = this.zoomScaleExtent || [0.15, 5];
            return [
                this.preserveNodeStyle,
                this.variableRadius,
                this.linkDistance,
                this.layoutTicks,
                this.layoutCharge,
                this.layoutClusterStrength,
                this.ringRadiusRatio,
                this.nodeLabelMaxLen,
                this.initialJitter,
                this.hoverDimOpacity,
                z[0],
                z[1],
                this.clusterKeyFn ? 1 : 0,
            ].join('|');
        },
    },
    mounted() {
        // 首次挂载后按容器尺寸渲染
        this.$nextTick(() => this.renderGraph());
    },
    beforeDestroy() {
        // 移除监听、清空 DOM，避免泄漏
        this.destroyD3Graph();
    },
    methods: {
        /**
         * 销毁当前画布与交互；父组件在截图前若需保证状态一致可主动调用
         */
        destroyD3Graph() {
            if (typeof this._graphTeardown === 'function') {
                this._graphTeardown();
                this._graphTeardown = null;
            }
            if (this._d3Simulation) {
                this._d3Simulation.stop();
                this._d3Simulation = null;
            }
            const el = this.$refs.wrap;
            if (el) {
                el.innerHTML = '';
            }
        },
        /**
         * 边标签透明度：与 sideLabel 联动
         * hidden — 默认 0，悬停相关边时为 1
         * show — 默认 0.5，悬停时 1
         */
        edgeLabelOpacity(num, type) {
            let opacity = num;
            if (this.sideLabel === 'show') {
                opacity = 0.5;
                if (type === 'hover') {
                    opacity = 1;
                }
            } else {
                opacity = num;
            }
            return opacity;
        },
        /** 按节点 degree（业务分类）设置 fill/stroke，未匹配则用默认色 */
        nodeFill(node) {
            let color = '#ff9898';
            const deg = node.degree;
            if (deg === '中医治则') {
                color = '#fca3d9';
            } else if (deg === '中医治疗') {
                color = '#e84dff';
            } else if (deg === '中医疾病') {
                color = '#c147fa';
            } else if (deg === '中医证候') {
                color = '#9a80ff';
            } else if (deg === '中医诊断') {
                color = '#c0c8fd';
            } else if (deg === '中药') {
                color = '#fdcece';
            } else if (deg === '临床表现') {
                color = '#3ba7fa';
            } else if (deg === '体格检查') {
                color = '#60f6fc';
            } else if (deg === '方剂') {
                color = '#78cea3';
            } else if (deg === '治法') {
                color = '#96fe82';
            } else if (deg === '症状') {
                color = '#dafe5c';
            } else if (deg === '穴位') {
                color = '#fbbd37';
            } else if (deg === '西医治疗') {
                color = '#fd9526';
            } else if (deg === '西医疾病') {
                color = '#ff6035';
            } else if (deg === '辅助检查') {
                color = '#bffaee';
            }
            if (!node.style) node.style = {};
            node.style.fill = color;
            node.style.stroke = color;
        },
        /**
         * 入口：清空旧图 → 读容器尺寸 → 无节点则退出
         */
        renderGraph() {
            this.destroyD3Graph();
            const nodesIn = (this.graphData && this.graphData.nodes) || [];
            if (!nodesIn.length) return;

            const wrap = this.$refs.wrap;
            if (!wrap) return;
            console.log(this.graphWidth,'=this.graphWidth')
            this.graphWidth = wrap.clientWidth || 1200;
            this.graphHeight = wrap.clientHeight || 700;

            this.graphTab();
        },
        /**
         * 核心：准备数据 → 力导向布局 → Canvas 绘制 → 绑定缩放/拖拽/悬停
         */
        graphTab() {
            const container = this.$refs.wrap;
            if (!container) return;

            const width = this.graphWidth;
            const height = this.graphHeight;
            const vm = this;

            const nodesIn = (this.graphData && this.graphData.nodes) || [];
            const edgesIn = (this.graphData && this.graphData.edges) || [];

            const nodeRadius = (d) => {
                if (vm.variableRadius) {
                    const r = d._r;
                    if (r != null && r > 0) return r;
                    return Math.max(4, (d.size || 10) / 2);
                }
                return 20;
            };

            // ---------- 1. 节点：拷贝 + 着色 + _nid；边：解析端点并过滤无效边 ----------
            const clusterKey = (d) => {
                if (vm.clusterKeyFn) return vm.clusterKeyFn(d);
                const k = d.degree;
                return k != null && k !== '' ? String(k) : '其他';
            };

            const rawNodes = nodesIn.map((n, i) => {
                const o = Object.assign({}, n);
                if (!o.style) o.style = {};
                if (vm.preserveNodeStyle) {
                    if (!o.style.fill) o.style.fill = '#3ba7fa';
                    if (!o.style.stroke) o.style.stroke = o.style.fill;
                } else {
                    vm.nodeFill(o);
                }
                if (vm.variableRadius && (o._r == null || o._r <= 0)) {
                    o._r = Math.max(4, (o.size || 10) / 2);
                }
                o._nid = nodeId(o, i);
                return o;
            });
            const nodeById = new Map(rawNodes.map((n) => [n._nid, n]));

            const rawLinks = edgesIn.map((e, i) => ({
                ...e,
                _sourceId: linkEndpointKey(e.source != null ? e.source : e.startNode, i),
                _targetId: linkEndpointKey(e.target != null ? e.target : e.endNode, i),
            })).filter(
                (l) =>
                    l._sourceId &&
                    l._targetId &&
                    nodeById.has(l._sourceId) &&
                    nodeById.has(l._targetId)
            );

            const nodes = rawNodes.map((n) => Object.assign({}, n));
            const links = rawLinks.map((l) => ({
                source: l._sourceId,
                target: l._targetId,
                relation: l.relation,
                label: l.label,
            }));

            // 邻接表：用于 hoverNode 时判断“相关/无关”
            const adjacency = new Map();
            const addAdj = (a, b) => {
                if (!adjacency.has(a)) adjacency.set(a, new Set());
                adjacency.get(a).add(b);
            };
            rawLinks.forEach((l) => {
                addAdj(l._sourceId, l._targetId);
                addAdj(l._targetId, l._sourceId);
            });

            // ---------- 2. 簇：按 degree 分簇，簇中心均匀分布在中心圆周上 ----------
            const clusterIds = [...new Set(nodes.map(clusterKey))].sort();
            const cx0 = width / 2;
            const cy0 = height / 2;
            const ringR = Math.min(width, height) * vm.ringRadiusRatio;
            const clusterCenters = {};
            const nC = clusterIds.length;
            clusterIds.forEach((id, i) => {
                const angle = (2 * Math.PI * i) / Math.max(nC, 1) - Math.PI / 2;
                clusterCenters[id] = {
                    x: cx0 + ringR * Math.cos(angle),
                    y: cy0 + ringR * Math.sin(angle),
                };
            });

            const jitter = vm.initialJitter;
            nodes.forEach((n) => {
                const c = clusterCenters[clusterKey(n)] || { x: cx0, y: cy0 };
                n.x = c.x + (Math.random() - 0.5) * jitter;
                n.y = c.y + (Math.random() - 0.5) * jitter;
            });

            // ---------- 3. 力导向：多步 tick 后固定 fx/fy，布局结束不再动画 ----------
            const str = vm.layoutClusterStrength;
            const simulation = d3.forceSimulation(nodes)
                .force('link', d3.forceLink(links).id((d) => d._nid).distance(vm.linkDistance))
                .force('charge', d3.forceManyBody().strength(vm.layoutCharge))
                .force('x', d3.forceX((d) => (clusterCenters[clusterKey(d)] || { x: cx0 }).x).strength(str))
                .force('y', d3.forceY((d) => (clusterCenters[clusterKey(d)] || { y: cy0 }).y).strength(str))
                .force('collision', d3.forceCollide().radius((d) => nodeRadius(d) + 2));

            this._d3Simulation = simulation;
            simulation.stop();
            const LAYOUT_TICKS = vm.layoutTicks;
            for (let i = 0; i < LAYOUT_TICKS; i++) {
                simulation.tick();
            }
            nodes.forEach((d) => {
                d.fx = d.x;
                d.fy = d.y;
            });
            simulation.stop();
            this._d3Simulation = null;

            // ---------- 4. Canvas：高分屏 dpr，逻辑坐标经 zoom transform 映射 ----------
            const dpr = window.devicePixelRatio || 1;
            const canvas = document.createElement('canvas');
            canvas.className = 'atlas-graph-canvas';
            canvas.style.display = 'block';
            canvas.style.cursor = 'default';
            canvas.width = Math.floor(width * dpr);
            canvas.height = Math.floor(height * dpr);
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            container.appendChild(canvas);

            const ctx = canvas.getContext('2d');
            let transform = d3.zoomIdentity;

            let hoverNode = null;
            let hoverEdge = null;
            let dragNode = null;

            /** 屏幕坐标 → 当前 zoom 下的图坐标 */
            const pointerToGraph = (e) => {
                const rect = canvas.getBoundingClientRect();
                const sx = e.clientX - rect.left;
                const sy = e.clientY - rect.top;
                return transform.invert([sx, sy]);
            };

            const hitNode = (gx, gy) => {
                for (let i = nodes.length - 1; i >= 0; i--) {
                    const d = nodes[i];
                    const r = nodeRadius(d);
                    const dx = gx - d.x;
                    const dy = gy - d.y;
                    if (dx * dx + dy * dy <= r * r) {
                        return d;
                    }
                }
                return null;
            };

            /** 边附近命中（距离阈值随缩放调整） */
            const hitLink = (gx, gy) => {
                const tol = 14 / transform.k;
                const tol2 = tol * tol;
                let best = null;
                let bestD = tol2;
                for (const ld of links) {
                    const s = ld.source;
                    const t = ld.target;
                    const d2 = dist2PointToSegment(gx, gy, s.x, s.y, t.x, t.y);
                    if (d2 <= bestD) {
                        bestD = d2;
                        best = ld;
                    }
                }
                return best;
            };

            /** 悬停边加粗；悬停节点时仅相邻边正常色，其余变淡 */
            const linkStroke = (ld) => {
                if (hoverEdge === ld) {
                    return { stroke: '#626161', lineW: 8, alpha: 1 };
                }
                if (hoverNode) {
                    const on = ld.source === hoverNode || ld.target === hoverNode;
                    if (on) {
                        return { stroke: '#626161', lineW: 1.5, alpha: 0.5 };
                    }
                    return {
                        stroke: '#626161',
                        lineW: 1.5,
                        alpha: Math.max(0, Math.min(1, vm.hoverDimOpacity)),
                    };
                }
                return { stroke: '#626161', lineW: 1.5, alpha: 0.5 };
            };

            const linkLabelOpacity = (ld) => {
                if (hoverEdge === ld) {
                    return vm.edgeLabelOpacity(1, 'hover');
                }
                if (hoverNode) {
                    const on = ld.source === hoverNode || ld.target === hoverNode;
                    return on
                        ? vm.edgeLabelOpacity(1, 'hover')
                        : vm.edgeLabelOpacity(0, 'hover');
                }
                return vm.edgeLabelOpacity(0);
            };

            /** 重绘一帧：先边后点（点盖在边上） */
            const redraw = () => {
                ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
                ctx.clearRect(0, 0, width, height);
                ctx.save();
                ctx.translate(transform.x, transform.y);
                ctx.scale(transform.k, transform.k);

                const dimAlpha = Math.max(0, Math.min(1, vm.hoverDimOpacity));
                const hoverId = hoverNode ? hoverNode._nid : null;
                const relatedSet = hoverId ? adjacency.get(hoverId) : null;
                const isRelatedNode = (d) => {
                    if (!hoverId) return true;
                    if (d._nid === hoverId) return true;
                    return relatedSet ? relatedSet.has(d._nid) : false;
                };

                for (const ld of links) {
                    const s = ld.source;
                    const t = ld.target;
                    const st = linkStroke(ld);
                    ctx.beginPath();
                    ctx.moveTo(s.x, s.y);
                    ctx.lineTo(t.x, t.y);
                    ctx.strokeStyle = st.stroke;
                    ctx.globalAlpha = st.alpha;
                    ctx.lineWidth = st.lineW;
                    ctx.stroke();
                    ctx.globalAlpha = 1;

                    const text = ld.relation || ld.label || '';
                    const op = linkLabelOpacity(ld);
                    if (text && op > 0.01) {
                        const mx = (s.x + t.x) / 2;
                        const my = (s.y + t.y) / 2;
                        ctx.font = '12px sans-serif';
                        ctx.fillStyle = `rgba(51,51,51,${op})`;
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillText(text, mx, my);
                    }
                }

                for (const d of nodes) {
                    const fill = (d.style && d.style.fill) || '#ff9898';
                    const stroke = (d.style && d.style.stroke) || fill;
                    const nodeAlpha = hoverId
                        ? isRelatedNode(d)
                            ? 1
                            : dimAlpha
                        : 1;
                    ctx.globalAlpha = nodeAlpha;
                    const r = nodeRadius(d);
                    ctx.beginPath();
                    ctx.arc(d.x, d.y, r, 0, Math.PI * 2);
                    ctx.fillStyle = fill;
                    ctx.fill();
                    ctx.strokeStyle = stroke;
                    ctx.lineWidth = (d.style && d.style.lineWidth) || (vm.preserveNodeStyle ? 2 : 1);
                    ctx.stroke();

                    let label = d.label || d.name || '';
                    if (label) {
                        const maxLen = vm.nodeLabelMaxLen;
                        if (maxLen > 0 && label.length > maxLen) {
                            label = `${label.slice(0, maxLen - 1)}…`;
                        }
                        ctx.font = vm.preserveNodeStyle ? '12px sans-serif' : '14px sans-serif';
                        ctx.fillStyle = '#000';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillText(label, d.x, d.y);
                    }
                }
                ctx.globalAlpha = 1;

                ctx.restore();
            };

            /** 拖拽节点 / 悬停节点或边 → 更新高亮并重绘 */
            const onPointerMove = (e) => {
                const [gx, gy] = pointerToGraph(e);
                if (dragNode) {
                    dragNode.x = dragNode.fx = gx;
                    dragNode.y = dragNode.fy = gy;
                    canvas.style.cursor = 'grabbing';
                    redraw();
                    return;
                }
                const hn = hitNode(gx, gy);
                if (hn) {
                    hoverNode = hn;
                    hoverEdge = null;
                    canvas.style.cursor = 'grab';
                    redraw();
                    return;
                }
                hoverNode = null;
                hoverEdge = hitLink(gx, gy);
                canvas.style.cursor = hoverEdge ? 'pointer' : 'default';
                redraw();
            };

            /** 仅在节点上按下时进入拖拽，避免与画布平移冲突 */
            const onPointerDown = (e) => {
                if (e.button !== 0) return;
                const [gx, gy] = pointerToGraph(e);
                const hn = hitNode(gx, gy);
                if (hn) {
                    dragNode = hn;
                    hoverNode = hn;
                    hoverEdge = null;
                    canvas.style.cursor = 'grabbing';
                    e.preventDefault();
                }
            };

            const onPointerUp = () => {
                if (dragNode) {
                    dragNode.fx = dragNode.x;
                    dragNode.fy = dragNode.y;
                    dragNode = null;
                    redraw();
                }
            };

            const onPointerLeave = () => {
                if (!dragNode) {
                    hoverNode = null;
                    hoverEdge = null;
                    canvas.style.cursor = 'default';
                    redraw();
                }
            };

            // ---------- 5. 缩放：左键在空白处拖动画布；在节点上左键留给拖拽节点 ----------
            const zoom = d3.zoom()
                .scaleExtent(vm.zoomScaleExtent || [0.15, 5])
                .filter((event) => {
                    if (event.type === 'wheel') return true;
                    if (event.type === 'mousedown' && event.button === 0) {
                        const [gx, gy] = pointerToGraph(event);
                        return !hitNode(gx, gy);
                    }
                    return !event.button;
                })
                .on('zoom', (event) => {
                    transform = event.transform;
                    redraw();
                });

            d3.select(canvas).call(zoom);
            redraw();

            // ---------- 6. DOM 事件（zoom 由 d3 接管 wheel；平移过滤见上） ----------
            canvas.addEventListener('mousemove', onPointerMove);
            canvas.addEventListener('mousedown', onPointerDown);
            window.addEventListener('mouseup', onPointerUp);
            canvas.addEventListener('mouseleave', onPointerLeave);

            /** 组件销毁或下次 renderGraph 时调用，解除监听 */
            this._graphTeardown = () => {
                canvas.removeEventListener('mousemove', onPointerMove);
                canvas.removeEventListener('mousedown', onPointerDown);
                window.removeEventListener('mouseup', onPointerUp);
                canvas.removeEventListener('mouseleave', onPointerLeave);
                d3.select(canvas).on('.zoom', null);
            };
        }
    }
};
</script>

<style scoped>
/* 外层需由父级给定高度（如 atlas 里 750px），否则 min-height 兜底 */
.d3-knowledge-graph {
    width: 100%;
    height: 100%;
    min-height: 200px;
}
</style>
